import { useState, useEffect } from 'react';
import {
  Col,
  Row,
  List,
  Utils,
  mapQuery,
  AddButton,
  FlexLayout,
  ListHeader,
  useNavigate,
  EllipsisText,
  useElementSize,
  ListHeaderWrap,
  useSearchPagination,
} from '@gv/triage-components';

import { CallTab } from 'types';
import { formatTimestamp } from 'utils/config';
import { useListQuery } from 'store/api/clients';
import { useGetHospitalQuery } from 'store/api/hospital-bitwerx';

import * as Styles from './styles';
import { ClientListProps, SelectClientFormValues } from './types';

export * from './config';
export type { SelectClientFormValues };

import { Routes } from 'config';
import { useAppDispatch } from 'store';
import { setIsHospitalEditEnabled } from 'store/slices/analytics-export';
const { textValue, formatPhone } = Utils.Helpers;

export const ClientList = ({
  formik,
  backTitle,
  hospitalId,
  newClientPath,
  template = '19% 19% 14% 1fr 1fr 1fr 1fr',
}: ClientListProps) => {
  const pageData = useSearchPagination();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [bitwerxStatus, setBitwerxStatus] = useState<boolean>(false);
  const { data: hospitalBitwerx } = useGetHospitalQuery({
    id: hospitalId ?? 0,
  });
  const getBitwerxStatus = async (response: any) => {
    if (
      response?.response?.bitwerx_practice_id ||
      response?.response?.pims_location_id
    ) {
      setBitwerxStatus(true);
      dispatch(
        setIsHospitalEditEnabled(response?.response?.is_hospital_edit_enabled)
      );
    }
  };
  useEffect(() => {
    getBitwerxStatus(hospitalBitwerx);
  }, [hospitalBitwerx]);
  const basePath = `${Routes.ActionCenter.Comm}/${CallTab.Consultation}`;
  const { errors, values, resetForm, setFieldValue } = formik;

  const [listWrapperRef, { width }] = useElementSize();

  const { currentPage: page, debounceQuery: search } = pageData;

  const { data, ...props } = useListQuery({
    page,
    search,
    hospitalId: String(hospitalId),
  });

  const { selectedClientId } = values;

  useEffect(() => {
    if (
      selectedClientId &&
      !data?.rows.some((client) => client.id == selectedClientId)
    ) {
      resetForm();
    }
  }, [data, selectedClientId, resetForm]);
  return (
    <List
      {...mapQuery(props)}
      count={data?.count}
      listWrapperRef={listWrapperRef}
      searchContainerStyle={{
        marginTop: 0,
      }}
      topHeader={
        <Styles.ListHeader>
          <Styles.BackButton text={backTitle} />
          <AddButton text="New client" to={newClientPath} />
        </Styles.ListHeader>
      }
      header={
        <ListHeaderWrap>
          {(scrollProps) => (
            <>
              {errors.selectedClientId && (
                <Styles.Error>{errors.selectedClientId as string}</Styles.Error>
              )}

              {width > 800 && (
                <ListHeader
                  template={template}
                  titles={[
                    'Client',
                    'Phone',
                    'Pet',
                    'Species',
                    'Date of Birth',
                    'Address',
                    ...(bitwerxStatus ? ['View PIMS Details'] : []),
                  ]}
                  {...scrollProps}
                />
              )}
            </>
          )}
        </ListHeaderWrap>
      }
      {...pageData}
    >
      {data?.rows?.map((client: any) => {
        const { is_pims_client } = client;
        if (is_pims_client) {
          const {
            id,
            phones,
            addresses,
            firstName,
            clientPatientRelationships,
          } = client;

          const formattedPhone = phones ? formatPhone(phones[0]?.number) : '';

          const commonProps = {
            key: id,
            selected: selectedClientId ? id === selectedClientId : false,
            onClick: () => {
              setFieldValue('isPimsRecord', true);
              setFieldValue('selectedClientId', id);
            },
          };

          return width > 800 ? (
            <Styles.ListCard
              gap={24}
              template={template}
              values={[
                <Styles.Name className="semibold">{firstName}</Styles.Name>,
                formattedPhone,
                clientPatientRelationships?.[0]?.patient?.name,
                clientPatientRelationships?.[0]?.patient?.species,
                clientPatientRelationships?.[0]?.patient?.dateOfBirth
                  ? formatTimestamp(
                      clientPatientRelationships?.[0]?.patient?.dateOfBirth,
                      'MM-DD-YYYY'
                    )
                  : '-',
                addresses?.[0]?.address1,
                <span
                  style={{
                    color: 'blue',
                  }}
                  onClick={() => {
                    navigate(
                      `${basePath}${Routes.ActionCenter.InComm.BitwerxDetail}`.replace(
                        ':clientId',
                        id + ''
                      )
                    );
                  }}
                >
                  Click Here
                </span>,
              ]}
              {...commonProps}
            />
          ) : (
            <Styles.Client {...commonProps}>
              <Styles.Name>{firstName}</Styles.Name>
              <Row>
                <Col col={6}>
                  <FlexLayout flexDirection="column">
                    <Styles.Label>Phone</Styles.Label>
                    <EllipsisText>{formattedPhone}</EllipsisText>
                  </FlexLayout>
                </Col>
                <Col col={6}>
                  <FlexLayout flexDirection="column">
                    <Styles.Label>Pet</Styles.Label>
                    <EllipsisText>
                      {textValue(
                        clientPatientRelationships?.[0]?.patient?.name
                      )}
                    </EllipsisText>
                  </FlexLayout>
                </Col>
                <Col col={6}>
                  <FlexLayout flexDirection="column">
                    <Styles.Label>Species</Styles.Label>
                    <EllipsisText>
                      {textValue(
                        clientPatientRelationships?.[0]?.patient?.species
                      )}
                    </EllipsisText>
                  </FlexLayout>
                </Col>
                <Col col={6}>
                  <FlexLayout flexDirection="column">
                    <Styles.Label>Date of Birth</Styles.Label>
                    <EllipsisText>
                      {textValue(
                        clientPatientRelationships?.[0]?.patient?.dateOfBirth
                          ? formatTimestamp(
                              clientPatientRelationships?.[0]?.patient
                                ?.dateOfBirth,
                              'MM-DD-YYYY'
                            )
                          : '-'
                      )}
                    </EllipsisText>
                  </FlexLayout>
                </Col>
              </Row>
              <Row>
                <Col>
                  <FlexLayout flexDirection="column">
                    <Styles.Label>Address</Styles.Label>
                    <EllipsisText>
                      {textValue(addresses?.[0]?.address1)}
                    </EllipsisText>
                  </FlexLayout>
                </Col>
              </Row>
              <Row>
                <Col>
                  <FlexLayout flexDirection="column">
                    <Styles.Label>PIMS Detail</Styles.Label>
                    <EllipsisText>
                      {textValue(addresses?.[0]?.address1)}
                    </EllipsisText>
                  </FlexLayout>
                </Col>
              </Row>
            </Styles.Client>
          );
        } else {
          const {
            id,
            pets,
            name,
            phone,
            address,
            bitwerx_client_id,
            vetspire_client_id,
          } = client;
          const formattedPhone = formatPhone(phone);

          const commonProps = {
            key: id,
            selected: selectedClientId ? id === selectedClientId : false,
            onClick: () => {
              setFieldValue(
                'bitwerx_client_id',
                bitwerx_client_id || vetspire_client_id
              );
              setFieldValue('isPimsRecord', false);
              setFieldValue('selectedClientId', id);
            },
          };

          return width > 800 ? (
            <Styles.ListCard
              gap={24}
              template={template}
              values={[
                <Styles.Name className="semibold">{name}</Styles.Name>,
                formattedPhone,
                pets?.[0]?.name,
                pets?.[0]?.species,
                pets?.[0]?.date_of_birth
                  ? formatTimestamp(
                      Number(pets?.[0]?.date_of_birth),
                      'MM-DD-YYYY'
                    )
                  : '-',
                address,
                ...(bitwerxStatus
                  ? [
                      bitwerx_client_id || vetspire_client_id ? (
                        <span
                          style={{
                            color: 'blue',
                          }}
                          onClick={() => {
                            navigate(
                              `${basePath}${Routes.ActionCenter.InComm.BitwerxDetail}`.replace(
                                ':clientId',
                                bitwerx_client_id || vetspire_client_id + ''
                              )
                            );
                          }}
                        >
                          Click Here
                        </span>
                      ) : (
                        '--'
                      ),
                    ]
                  : []),
              ]}
              {...commonProps}
            />
          ) : (
            <Styles.Client {...commonProps}>
              <Styles.Name>{name}</Styles.Name>
              <Row>
                <Col col={6}>
                  <FlexLayout flexDirection="column">
                    <Styles.Label>Phone</Styles.Label>
                    <EllipsisText>{formattedPhone}</EllipsisText>
                  </FlexLayout>
                </Col>
                <Col col={6}>
                  <FlexLayout flexDirection="column">
                    <Styles.Label>Pet</Styles.Label>
                    <EllipsisText>{textValue(pets?.[0]?.name)}</EllipsisText>
                  </FlexLayout>
                </Col>
              </Row>
              <Row>
                <Col>
                  <FlexLayout flexDirection="column">
                    <Styles.Label>Address</Styles.Label>
                    <EllipsisText>{textValue(address)}</EllipsisText>
                  </FlexLayout>
                </Col>
              </Row>
            </Styles.Client>
          );
        }
      })}
    </List>
  );
};
